// src/belediye/pages/profile/PasswordChangeSection.js
//
// Minimal Security Flow (ProfilePage içinde)
// - CSS: ProfilePage.css + ProfilePage.mobile.css
// - Stage: input -> verify -> success
// - verify stage'e sadece "kod gönderme başarılı" olursa geçer
// - current_password payload'a eklendi (backend doğrulaması için şart)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  sendVerificationCode,
  changePassword,
  clearProfileErrors,
  fetchMyProfile,
} from "../../redux/slices/profileSlice";

import {
  ArrowPathIcon,
  KeyIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

import "./ProfilePage.css";
import "./ProfilePage.mobile.css";

const PASSWORD_MIN_LEN = 8;
const CODE_EXPIRE_SEC = 5 * 60;

export default function PasswordChangeSection({
  phoneNumber: propPhoneNumber,
  userId: propUserId,
  onSuccess,
  onCancel, // opsiyonel: parent kapatmak isterse
}) {
  const dispatch = useDispatch();

  const {
    user,
    sendCodeLoading,
    sendCodeError,
    sendCodeSuccess,
    passwordChangeLoading,
    passwordChangeError,
    passwordChangeSuccess,
  } = useSelector((state) => state.profile);

  const userId = propUserId ?? user?.id ?? null;
  const phoneNumber = propPhoneNumber ?? user?.phone_number ?? "";

  // stages: input -> verify -> success
  const [stage, setStage] = useState("input");

  // inputs
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [code, setCode] = useState("");

  // local error
  const [formError, setFormError] = useState(null);

  // timer
  const [timer, setTimer] = useState(CODE_EXPIRE_SEC);
  const timerRef = useRef(null);

  // ensure me + cleanup
  useEffect(() => {
    if (!user?.id) dispatch(fetchMyProfile());
    dispatch(clearProfileErrors());

    return () => {
      dispatch(clearProfileErrors());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [dispatch, user?.id]);

  const resetAll = () => {
    setFormError(null);
    setCurrentPassword("");
    setNewPassword("");
    setRepeatPassword("");
    setCode("");
    setTimer(CODE_EXPIRE_SEC);
    dispatch(clearProfileErrors());
  };

  // timer runs only on verify
  useEffect(() => {
    if (stage !== "verify") {
      setTimer(CODE_EXPIRE_SEC);
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    setTimer(CODE_EXPIRE_SEC);
    timerRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [stage]);

  // success
  useEffect(() => {
    if (!passwordChangeSuccess) return;

    setStage("success");
    if (timerRef.current) clearInterval(timerRef.current);

    const t = setTimeout(() => {
      onSuccess?.();
      resetAll();
      setStage("input");
    }, 1200);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordChangeSuccess]);

  // validation message (input stage)
  const validationMessage = useMemo(() => {
    if (stage !== "input") return null;

    if (!currentPassword && !newPassword && !repeatPassword) return null;

    if (!currentPassword) return "Mevcut şifre zorunludur.";
    if (!newPassword) return "Yeni şifre zorunludur.";
    if (!repeatPassword) return "Yeni şifre tekrar zorunludur.";

    if (newPassword.length < PASSWORD_MIN_LEN) {
      return `Yeni şifre en az ${PASSWORD_MIN_LEN} karakter olmalı.`;
    }
    if (newPassword !== repeatPassword) return "Yeni şifreler uyuşmuyor.";
    if (newPassword === currentPassword) return "Yeni şifre eski şifreyle aynı olamaz.";

    const hasLetter = /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(newPassword);
    const hasDigit = /\d/.test(newPassword);
    const hasSymbol = /[^A-Za-z0-9ÇĞİÖŞÜçğıöşü]/.test(newPassword);
    if (!hasLetter || !hasDigit || !hasSymbol) {
      return "Yeni şifre en az bir harf, bir rakam ve bir işaret içermeli.";
    }

    return null;
  }, [stage, currentPassword, newPassword, repeatPassword]);

  const canSendCode = useMemo(() => {
    return !!userId && !!phoneNumber && !validationMessage && !sendCodeLoading;
  }, [userId, phoneNumber, validationMessage, sendCodeLoading]);

  const canVerify = useMemo(() => {
    return !!userId && !!phoneNumber && code?.length >= 6 && !passwordChangeLoading;
  }, [userId, phoneNumber, code, passwordChangeLoading]);

  const formatTimer = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleSendCode = async (e) => {
    e.preventDefault();

    dispatch(clearProfileErrors());
    if (!userId) return setFormError("Kullanıcı bilgisi yüklenemedi. Sayfayı yenileyin.");
    if (!phoneNumber) return setFormError("Telefon numarası bulunamadı.");
    if (validationMessage) return setFormError(validationMessage);

    setFormError(null);

    const res = await dispatch(
      sendVerificationCode({
        user_id: userId,
        phone_number: phoneNumber,
        purpose: "password_reset",
      })
    );

    if (res.meta?.requestStatus === "fulfilled") {
      setStage("verify");
    } else {
      setStage("input");
    }
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();

    dispatch(clearProfileErrors());
    if (!userId) return setFormError("Kullanıcı bilgisi yüklenemedi. Sayfayı yenileyin.");
    if (!phoneNumber) return setFormError("Telefon numarası bulunamadı.");
    if (!code || code.length < 6) return setFormError("Kod en az 6 haneli olmalı.");

    setFormError(null);

    dispatch(
      changePassword({
        user_id: userId,
        phone_number: phoneNumber,
        code,
        new_password: newPassword,
        current_password: currentPassword, //   backend doğrulaması için gerekli
      })
    );
  };

  const handleResend = () => {
    if (!userId || !phoneNumber) return;

    dispatch(clearProfileErrors());
    dispatch(
      sendVerificationCode({
        user_id: userId,
        phone_number: phoneNumber,
        purpose: "password_reset",
      })
    );

    setTimer(CODE_EXPIRE_SEC);
  };

  const backToInput = () => {
    dispatch(clearProfileErrors());
    setFormError(null);
    setStage("input");
  };

  const handleCancel = () => {
    resetAll();
    setStage("input");
    onCancel?.();
  };

  // ------------------------
  // UI
  // ------------------------
  return (
    <div className="settings-section">
      {/* INPUT */}
      {stage === "input" && (
        <div className="settings-panel">
          

          <form className="settings-form" onSubmit={handleSendCode} autoComplete="off">
            <div className="settings-field">
              <label className="settings-field-label">Mevcut Şifre</label>
              <input
                className="md-input"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Şu anki şifreniz"
                autoComplete="current-password"
                required
              />
            </div>

            <div className="settings-field">
              <label className="settings-field-label">Yeni Şifre</label>
              <input
                className="md-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Yeni şifre"
                autoComplete="new-password"
                required
              />
            </div>

            <div className="settings-field">
              <label className="settings-field-label">Yeni Şifre (Tekrar)</label>
              <input
                className="md-input"
                type="password"
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
                placeholder="Yeni şifre (tekrar)"
                autoComplete="new-password"
                required
              />
            </div>

            <div className="settings-note">
              <KeyIcon className="settings-note-ico" />
              En az {PASSWORD_MIN_LEN} karakter; harf + rakam + işaret.
            </div>

            {(formError || validationMessage || sendCodeError) && (
              <div className="settings-alert settings-alert-error">
                {formError || validationMessage || sendCodeError}
              </div>
            )}

            {sendCodeSuccess && (
              <div className="settings-alert settings-alert-ok">{sendCodeSuccess}</div>
            )}

            <button type="submit" className="settings-primary" disabled={!canSendCode}>
              <ChatBubbleLeftRightIcon className="settings-btn-ico" />
              {sendCodeLoading ? "Kod Gönderiliyor..." : "SMS Kodunu Gönder"}
            </button>
          </form>
        </div>
      )}

      {/* VERIFY */}
      {stage === "verify" && (
        <div className="settings-panel">
          <div className="settings-panel-top">
            <div className="settings-panel-title">SMS Doğrulama</div>
            <button type="button" className="settings-tertiary" onClick={handleCancel}>
              Kapat
            </button>
          </div>

          <div className="settings-verify-meta">
            <div className="settings-verify-row">
              <div className="settings-verify-k">Telefon</div>
              <div className="settings-verify-v">{phoneNumber || "-"}</div>
            </div>
            <div className="settings-verify-row">
              <div className="settings-verify-k">Süre</div>
              <div className="settings-verify-v">{formatTimer(timer)}</div>
            </div>
          </div>

          <form className="settings-form" onSubmit={handleVerifyCode} autoComplete="off">
            <div className="settings-field">
              <label className="settings-field-label">Kod</label>
              <input
                className="md-input"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="6 haneli kod"
                maxLength={8}
                inputMode="numeric"
                required
              />
            </div>

            {(formError || passwordChangeError) && (
              <div className="settings-alert settings-alert-error">
                {formError || passwordChangeError}
              </div>
            )}

            <div className="settings-actions">
              <button type="button" className="settings-tertiary" onClick={backToInput}>
                Geri
              </button>

              {timer === 0 ? (
                <button
                  type="button"
                  className="settings-tertiary"
                  onClick={handleResend}
                  disabled={sendCodeLoading}
                >
                  <ArrowPathIcon className="settings-btn-ico" />
                  Tekrar Gönder
                </button>
              ) : (
                <span className="settings-muted">
                  Kod gelmediyse süre dolunca tekrar gönderebilirsiniz.
                </span>
              )}
            </div>

            <button type="submit" className="settings-primary" disabled={!canVerify}>
              {passwordChangeLoading ? "Değiştiriliyor..." : "Şifreyi Değiştir"}
            </button>
          </form>
        </div>
      )}

      {/* SUCCESS */}
      {stage === "success" && (
        <div className="settings-panel settings-success">
          <CheckCircleIcon className="settings-success-ico" />
          <div className="settings-success-title">Şifre güncellendi</div>
          <div className="settings-success-sub">Yeni şifrenizi güvenli saklayın.</div>
        </div>
      )}
    </div>
  );
}
